import React, { useState } from 'react';
import { Table, TableColumnsType, Button, Space, Modal, message, Tag } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import {
  GET_ADDRESS_BLACKLIST_LIST,
  DELETE_ADDRESS_BLACKLIST,
  GetAddressBlacklistListResponse,
  AddressBlacklistType,
} from './graphql/addressBlacklistGraphql';

const AddressBlacklistPage: React.FC = () => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { t } = useTranslation();

  const { data, loading, refetch } = useQuery<GetAddressBlacklistListResponse>(
    GET_ADDRESS_BLACKLIST_LIST,
    {
      variables: {
        status: appliedFilters.status || undefined,
        fullAddress: appliedFilters.fullAddress || undefined,
        page: currentPage,
        size: pageSize,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [deleteBlacklist] = useMutation(DELETE_ADDRESS_BLACKLIST, {
    onCompleted: () => refetch(),
    onError: (err) => message.error(err.message),
  });

  const handleFilter = () => {
    setAppliedFilters({ ...filterOptions });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilterOptions(DEFAULT_FILTER_OPTIONS);
    setAppliedFilters(DEFAULT_FILTER_OPTIONS);
    setCurrentPage(1);
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const showDeleteConfirm = (record: AddressBlacklistType) => {
    Modal.confirm({
      title: t('mapTool.addressBlacklist.deleteTitle') as string,
      content: t('mapTool.addressBlacklist.deleteContent') as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: () => deleteBlacklist({ variables: { id: Number(record.id) } }),
    });
  };

  const columns: TableColumnsType<AddressBlacklistType> = [
    {
      title: t('mapTool.addressBlacklist.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: t('mapTool.addressBlacklist.table.standardizedFullAddress'),
      dataIndex: 'standardizedFullAddress',
      key: 'standardizedFullAddress',
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.addressBlacklist.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (v: string) => (
        <Tag color={v === 'active' ? 'green' : 'default'}>
          {t(`mapTool.addressBlacklist.status.${v}`) as string}
        </Tag>
      ),
    },
    {
      title: t('mapTool.addressBlacklist.table.blacklistType'),
      dataIndex: 'blacklistType',
      key: 'blacklistType',
      width: 120,
      align: 'center',
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.addressBlacklist.table.addressId'),
      dataIndex: 'addressId',
      key: 'addressId',
      width: 100,
      align: 'center',
    },
    {
      title: t('mapTool.addressBlacklist.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      align: 'center',
      render: (v: string | null | undefined) =>
        v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—',
    },
    {
      title: t('components.button.action') as string,
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <HasResource resourceCodes={[ResourceCode.addressBlacklist.DELETE]}>
            <Button
              type="link"
              size="small"
              danger
              onClick={() => showDeleteConfirm(record)}
            >
              {t('components.button.delete')}
            </Button>
          </HasResource>
        </Space>
      ),
    },
  ];

  const pageData = data?.addressBlacklistList;

  return (
    <div className="p-6">
      <h4 className="text-xl font-bold mb-4">{t('mapTool.addressBlacklist.pageTitle')}</h4>

      <FilterArea filters={filterOptions} setFilters={setFilterOptions} />

      <div className="flex justify-end items-center mb-4 mt-4">
        <div className="flex items-center gap-2">
          <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button onClick={handleResetFilters} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: pageData?.totalElements || 0,
            showSizeChanger: true,
            showTotal: (total) =>
              t('mapTool.addressBlacklist.table.paginationTotal', { total }),
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
        />
      </div>

    </div>
  );
};

export default AddressBlacklistPage;